import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Row, Col, Collapse } from 'antd';
import GlobalButton from 'Components/common/components/GlobalButton';
import { IsMobile, IsTablet, parseColorGradientMixture } from 'Utils/UtilityFunctions';
import {
  maxMobile,
  maxTablet,
  maxWidth, primaryColor, primaryFont, primaryHeadingColor, retina, secondaryFont, wMBold,
} from 'Utils/utilsStyle';
import PeopleIcon from 'Components/common/assets/svgIcons/PeopleIcon';
import IndustriesWidget from 'Components/common/components/IndustriesWidget';
import { useDispatch, useSelector } from 'react-redux';
import SkeletonCustom from 'Components/common/skeleton/SkeletonCustom';
import { actions } from 'Slice/ServiceLinesSlice';
import PeopleWidgetWithOutHolo from 'Components/common/components/PeopleWidgetWithOutHolo';
import { selectLoading, selectRecommendedServiceLines, selectServiceLinesEmpty } from 'Selector/ServiceLinesSelector';
import { ServiceLine } from 'Types/ServiceLinesTypes';
import BackIcon from 'Components/common/assets/svgIcons/BackIcon';

const StyleWrapper = styled.div`
  padding-top: 70px;
  padding-bottom: 70px;
  .fancy-box {
    border-radius: 10px;
    box-shadow: 0 0 60px rgba(0, 0, 0, 0.14);
    background: rgb(255, 255, 255);
    padding: 23px;
    @media all and (max-width: ${maxWidth}) , ${retina} {
     padding: 17px 25px 25px 25px;
    }
    @media all and (max-width: ${maxMobile}) {
     padding: 17px 16px 16px 16px;
    }

    h1 {
      font-family: ${secondaryFont};
      color: ${primaryHeadingColor};
      font-size: 22px;
      margin-bottom: 0;
      margin-left: 10px;
      @media all and (max-width: ${maxWidth}) , ${retina} {
        font-size: 19px;
      }
    }
    p {
      font-family: ${primaryFont};
      color: #5e7079;
    }
    input {
      font-size: 16px;
      padding: 7px 12px;
    }
    label {
      font-size: 12px;
    }
    .companiesSection {
      margin-top: 20px;
    }
    h4.recommended-name{
      color:${primaryHeadingColor};
      font-weight: ${wMBold};
      font-family:${secondaryFont};
      margin-bottom:0;
      font-size:22px;
      line-height: 38px;
      @media all and (max-width: ${maxWidth}) , ${retina} {
       font-size: 18px;
      }
      @media all and (max-width: ${maxTablet}) {
        font-size: 14px;
        line-height: 19px;
      }
    }
    .ant-collapse-header, .ant-collapse-content-box {
      padding: 0px !important;
      align-items: center;
    }
    .ant-collapse-header {
      @media all and (max-width: ${maxMobile}) {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
      }
      .ant-collapse-extra {
        @media all and (max-width: ${maxMobile}) {
          width: 100%;
        }
        button {
          @media all and (max-width: ${maxMobile}) {
            width: 100%;
            text-align: center;
            justify-content: center;
            margin-top: 15px;
          }
        }
      }
    }
    .ant-collapse-item {
      .ant-collapse-arrow {
        display: none !important;
      }
      .heading-collapse-c {
        .heading-icon {
          display: none;
        }
      }
      .see-less-btn {
        display: none;
      }
      &.ant-collapse-item-active {
        .see-less-btn {
          display: block;
           @media all and (max-width: ${maxMobile}) {
            display: none;
          }
        }
        .user-images {
          display: none;
        }
        .heading-collapse-c {
          .heading-icon {
            display: block;
            margin-right: 15px;
            margin-top: 5px;
          }
        }
        .ant-collapse-extra {
          .see-more-btn {
            display: none;
          }
        }
      }
    }
    .user-images{
    position: relative;
    margin-right:13px;
    z-index:0;
    > div{
        padding:2px !important;
        &:nth-child(1){
          z-index:5;
        }
        &:nth-child(2){
          z-index:4;
          margin-left: -10px;
        }
        &:nth-child(3){
          z-index:3;
          margin-left: -10px;
        }
        &:nth-child(4){
          z-index:2;
          margin-left: -10px;
        }
        &:nth-child(5){
          z-index:1;
          margin-left: -10px;
        }
    }
    .image-place {
      width: 40px;
      height: 40px;
      min-width: 40px;
      border-radius: 50%;
      position: relative;
      margin: 0px;
      padding: 2px !important;
      &:hover {
        .image-place-name {
          visibility: visible;
        }
      }
      .image-place-img {
        height: 100%;
        width: 100%;
        border-radius: 50%;
      }
      .image-place-name {
        font-size: 14px;
        font-family: ${primaryFont};
        font-weight: ${wMBold};
        color: rgb(126, 126, 126);
        text-align: center;
        line-height: 26px;
        position: absolute;
        left: 50%;
        bottom: -30px;
        margin-bottom: 6px;
        transform: translateX(-50%);
        width: max-content;
        visibility: hidden;
      }
    }
  }
  }
`;

const SLRecommended = function SLRecommended() {
  const { Panel } = Collapse;
  const [showSection, setShowSection] = useState(true);
  const servicelines = useSelector(selectRecommendedServiceLines);
  const emptyServicelines = useSelector(selectServiceLinesEmpty);
  const loading = useSelector(selectLoading);
  const [open, setOpen] = useState(false);
  const dispatch = useDispatch();
  const isTab = IsTablet();
  const isMob = IsMobile();

  const followServiceline = (serviceline: ServiceLine) => {
    // Prevent multiple clicks on the same item
    if (serviceline.loading || serviceline.added) {
      return;
    }
    dispatch(actions.addingServiceLine(serviceline));
  };

  const handleChange = () => {
    setOpen(!open);
  };

  useEffect(() => {
    if (!loading && !servicelines?.length) setShowSection(false);
    else setShowSection(true);
  }, [loading, servicelines]);

  return (showSection
    ? (
      <StyleWrapper>
        <div className="fancy-box">
          <Collapse ghost onChange={handleChange} activeKey={emptyServicelines || open ? ['1'] : ['']}>
            <Panel
              header={(
                <div className="d-flex heading-collapse-c">
                  <div className="heading-icon">
                    {isMob ? (
                      <BackIcon />
                    ) : (
                      <PeopleIcon />
                    )}
                  </div>
                  <div className="d-flex align-items-center user-images">
                    {
                      loading ? (
                        <>
                          {[1, 2, 3, 4, 5].map(() => <SkeletonCustom width="40px" height="40px" diameter="50%" margin="0px" />)}
                        </>
                      )
                        : servicelines.slice(0, 4).map((v) => {
                          return (
                            <div className='image-place'>
                              <img src={v.image} alt={v.name} className='image-place-img' />
                              <span className='image-place-name'>{v.name}</span>
                            </div>
                          );
                        })
                    }

                  </div>
                  <h4
                    className="recommended-name d-flex align-items-center"
                  >
                    {loading ? (<SkeletonCustom width={isTab ? '300px' : '500px'} height="20px" margin="0px" />) : 'We’ve found some Service Lines you might be interested in…'}

                  </h4>

                </div>
              )}
              key="1"
              extra={(
                <>
                  {!loading
                    ? <GlobalButton buttonClassName="see-more-btn" title="See Recommended" bgColor="#001760" borderRadius="25px" PaddingSm="5px 15px 5px 10px" /> : null}
                  {emptyServicelines ? null : <GlobalButton buttonClassName="see-less-btn" title="See Less Recommended" bgColor="#001760" borderRadius="25px" PaddingSm="5px 15px 5px 10px" />}
                </>
              )}
            >
              <div className="companiesSection">
                <Row className="Grid-width-row white-layer-col py-2" gutter={[24, 24]}>
                  {servicelines.map((serviceline) => (
                    <Col xxl={{ span: 4 }} xl={{ span: 4 }} lg={{ span: 6 }} className="gutter-row">
                      <IndustriesWidget
                        key={serviceline.id}
                        id={serviceline.id}
                        articleText={serviceline.name}
                        articleBanner={serviceline.image}
                        isAdded={serviceline.added}
                        followIndustry={() => followServiceline(serviceline)}
                        followLoading={serviceline.loading}
                        isServicelines
                        pagination={serviceline.pagination}
                      />
                    </Col>
                  ))}
                </Row>
              </div>
            </Panel>
          </Collapse>
        </div>
      </StyleWrapper>
    )
    : null
  );
};

export default SLRecommended;